import type { IContentBlock, IReaderDocument, IReaderPublication, TextContentBlockType } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { CirrusReaderLandmark } from "./CirrusReaderLandmark";
import { CirrusReaderPublicationLandmarkType, type ICirrusReaderDocumentLandmarkCollection, type ICirrusReaderPublicationLandmark, type ICirrusReaderReadingProgressionTimeline } from "./CirrusReaderModel";
import { TreeNodeWalker, type IAttributeData } from "@colibrio/colibrio-reader-framework/colibrio-core-base";

export const useCirrusReaderLandmarkCollection = (timeline?: ICirrusReaderReadingProgressionTimeline) => {
    const _documentIndexToLandmarksMap: Map<number, ICirrusReaderDocumentLandmarkCollection> = new Map();
    const _landmarkToDocumentIndex: Map<CirrusReaderLandmark, number> = new Map();
    const _blockIdToLandmarkMap: Map<string, CirrusReaderLandmark> = new Map();
    let _hasBeenPopulated: boolean = false;

    const { $cirrusReaderPublication } = useNuxtApp();

    const populate = async (documentIndexes?: number[], landmarkTypes?: CirrusReaderPublicationLandmarkType[]): Promise<void> =>  {
        await _populate($cirrusReaderPublication.colibrioReaderPublication, documentIndexes, landmarkTypes).catch(console.error);
    }

    const getAll = (): ICirrusReaderPublicationLandmark => {

        const publicationLandmarkData: ICirrusReaderPublicationLandmark = {
            publicationId: $cirrusReaderPublication.colibrioReaderPublication.getSourcePublication().getMetadata().getIdentifiers()[0]?.content.value.toString() ?? "",
            documents: [],
        };
        const landmarks: ICirrusReaderDocumentLandmarkCollection[] = [];

        _documentIndexToLandmarksMap.forEach((
            _doc: ICirrusReaderDocumentLandmarkCollection,
            key: number,
        ) => {
            const landmark = _documentIndexToLandmarksMap!.get(key);
            if (landmark && landmarks.findIndex(item => item === landmark) < 0) {
                landmarks.push(landmark);
            }
        });
        publicationLandmarkData.documents = landmarks;
        return publicationLandmarkData;
    }

    const getHeadings = (level?: TextContentBlockType): CirrusReaderLandmark[] | undefined => {
        const filteredResult: CirrusReaderLandmark[] = Array.from(_landmarkToDocumentIndex.keys());
        const headings = filteredResult.filter((item) => {
            if (level) {
                return item.blockType.indexOf(level) >= 0;
            } else {
                return item.blockType.indexOf('HEADING') >= 0;
            }
        });
        return headings.length > 0 ? headings : undefined;
    }

    const getFootnotes = (): CirrusReaderLandmark[] | undefined => {
        const filteredResult: CirrusReaderLandmark[] = Array.from(_landmarkToDocumentIndex.keys());
        const footnotes = filteredResult.filter((item) => {
            return item.subType && item.subType.toString().indexOf('FOOTNOTE') >= 0;
        });

        return footnotes.length > 0 ? footnotes : undefined;
    }

    const getFigures = (): CirrusReaderLandmark[] | undefined => {
        const filteredResult: CirrusReaderLandmark[] = Array.from(_landmarkToDocumentIndex.keys());
        const figures = filteredResult.filter((item) => {
            return item.blockType.includes('IMAGE') || item.blockType.includes('FIGURE');
        });
        return figures.length > 0 ? figures : undefined;
    }

    const getTables = (): CirrusReaderLandmark[] | undefined => {
        const filteredResult: CirrusReaderLandmark[] = Array.from(_landmarkToDocumentIndex.keys());
        const tables = filteredResult.filter((item) => {
            return item.blockType === 'TABLE';
        });
        return tables.length > 0 ? tables : undefined;
    }

    const getLists = (): CirrusReaderLandmark[] | undefined => {
        const filteredResult: CirrusReaderLandmark[] = Array.from(_landmarkToDocumentIndex.keys());
        const lists = filteredResult.filter((item) => {
            return item.blockType.includes('_LIST');
        });
        return lists.length ? lists : undefined;
    }

    const getType = (landmarkType: CirrusReaderPublicationLandmarkType): CirrusReaderLandmark[] | undefined => {
        const filteredResult: CirrusReaderLandmark[] = Array.from(_landmarkToDocumentIndex.keys());
        const lists = filteredResult.filter((item) => {
            return useCirrusReaderUtils().isCirrusReaderPublicationLandmarkType(item.blockType) &&
                item.blockType === landmarkType;
        });
        return lists.length ? lists : undefined;
    }

    const _populate = async (
        readerPublication: IReaderPublication,
        documentIndexes?: number[],
        landmarkTypesToInclude?: CirrusReaderPublicationLandmarkType[],
    ): Promise<void> => {

        if (_hasBeenPopulated) {
            return;
        }

        let documentsToSearch = readerPublication.getSpine();

        // Check if we should search only some elements as listed in the `documentIndexes` argument.
        if (documentIndexes) {
            documentsToSearch = readerPublication.getSpine().filter((doc: IReaderDocument) => {
                return documentIndexes.includes(doc.getSourceContentDocument().getIndexInSpine());
            });
        }

        for (const doc of documentsToSearch) {
            const documentIndex = doc.getSourceContentDocument().getIndexInSpine();

            if (_documentIndexToLandmarksMap!.has(documentIndex)) {
                continue;
            }

            const docLandmarks: ICirrusReaderDocumentLandmarkCollection = {
                documentIndex,
            };

            const contentBlockTree = await doc.fetchContentBlockTree();

            const walker = new TreeNodeWalker(contentBlockTree.getContentBlocks());

            while (walker.hasNext()) {
                let shouldAddBlock: boolean = false;
                const block = walker.next();
                const blockType = (block.getBlockType() as unknown) as CirrusReaderPublicationLandmarkType;
                let collectionToAddTo: CirrusReaderLandmark[] | undefined;
                let currentLandmarkData: CirrusReaderLandmark | undefined;

                if (!landmarkTypesToInclude) {
                    landmarkTypesToInclude = (Object.values(CirrusReaderPublicationLandmarkType) as unknown) as CirrusReaderPublicationLandmarkType[];
                }

                if (landmarkTypesToInclude.includes(blockType)) {
                    switch (blockType) {
                        case CirrusReaderPublicationLandmarkType.ARTICLE:
                            shouldAddBlock = true;
                            docLandmarks.articles = [];
                            collectionToAddTo = docLandmarks.articles;
                            break;
                        case CirrusReaderPublicationLandmarkType.SECTION:
                            shouldAddBlock = true;
                            docLandmarks.sections = [];
                            collectionToAddTo = docLandmarks.sections;
                            break;
                        case CirrusReaderPublicationLandmarkType.ASIDE:
                            shouldAddBlock = true;
                            docLandmarks.asides = undefined;
                            collectionToAddTo = docLandmarks.asides;
                            break;
                        case CirrusReaderPublicationLandmarkType.HEADING1:
                        case CirrusReaderPublicationLandmarkType.HEADING2:
                        case CirrusReaderPublicationLandmarkType.HEADING3:
                        case CirrusReaderPublicationLandmarkType.HEADING4:
                        case CirrusReaderPublicationLandmarkType.HEADING5:
                        case CirrusReaderPublicationLandmarkType.HEADING6:
                            shouldAddBlock = true;
                            docLandmarks.headings = [];
                            collectionToAddTo = docLandmarks.headings;
                            break;
                        case CirrusReaderPublicationLandmarkType.AUDIO:
                            shouldAddBlock = true;
                            docLandmarks.audio = [];
                            collectionToAddTo = docLandmarks.audio;
                            break;
                        case CirrusReaderPublicationLandmarkType.VIDEO:
                            shouldAddBlock = true;
                            docLandmarks.video = [];
                            collectionToAddTo = docLandmarks.video;
                            break;
                        case CirrusReaderPublicationLandmarkType.IMAGE:
                        case CirrusReaderPublicationLandmarkType.FIGURE:
                            shouldAddBlock = true;
                            docLandmarks.figures = [];
                            collectionToAddTo = docLandmarks.figures;
                            break;
                        case CirrusReaderPublicationLandmarkType.TABLE:
                            shouldAddBlock = true;
                            docLandmarks.tables = [];
                            collectionToAddTo = docLandmarks.tables;
                            break;
                        case CirrusReaderPublicationLandmarkType.DESCRIPTION_LIST:
                        case CirrusReaderPublicationLandmarkType.UNORDERED_LIST:
                        case CirrusReaderPublicationLandmarkType.ORDERED_LIST:
                            shouldAddBlock = true;
                            docLandmarks.lists = [];
                            collectionToAddTo = docLandmarks.lists;
                            break;
                        case CirrusReaderPublicationLandmarkType.LINK:
                            shouldAddBlock = true;
                            docLandmarks.links = [];
                            collectionToAddTo = docLandmarks.links;
                            break;
                        default:
                    }
                }

                if (landmarkTypesToInclude.includes(CirrusReaderPublicationLandmarkType.FOOTNOTE)) {
                    docLandmarks.footnotes = [];

                    if (hasContentBlockFootnoteAttribute(block)) {
                        shouldAddBlock = true;
                        currentLandmarkData = await _fetchLandmarkForContentBlock(block);
                        docLandmarks.footnotes.push(currentLandmarkData);
                    }
                }

                if (shouldAddBlock) {

                    collectionToAddTo = collectionToAddTo ? collectionToAddTo : [];
                    if (!currentLandmarkData) {
                        currentLandmarkData = await _fetchLandmarkForContentBlock(block);
                    }

                    collectionToAddTo.push(currentLandmarkData);

                    _landmarkToDocumentIndex.set(currentLandmarkData, documentIndex);

                    const blockIdAttribute = block.getAttributes().find((attribute: IAttributeData) => {
                        return attribute.localName === 'id';
                    });

                    if (blockIdAttribute && !_blockIdToLandmarkMap.has(blockIdAttribute.value)) {
                        _blockIdToLandmarkMap.set(blockIdAttribute.value, currentLandmarkData);
                    }

                    currentLandmarkData = undefined;
                }
            }

            _documentIndexToLandmarksMap!.set(documentIndex, docLandmarks);

            _hasBeenPopulated = true;

        }
    }

    const hasContentBlockFootnoteAttribute = (contentBlock: IContentBlock): boolean => {
        return contentBlock.getAttributes().some((attribute: IAttributeData) => {
            return (attribute.localName === 'role' && attribute.value.includes('doc-footnote'))
                || (attribute.localName === 'role' && attribute.value.includes('doc-endnote'))
                || (attribute.localName === 'type' && attribute.value.includes('footnote'))
                || (attribute.localName === 'type' && attribute.value.includes('endnote'))
                || (attribute.localName === 'type' && attribute.value.includes('rearnote'));

        });
    }

    const _fetchLandmarkForContentBlock = async (
        block: IContentBlock,
    ): Promise<CirrusReaderLandmark> => {
        return new CirrusReaderLandmark(block, $cirrusReaderPublication.navigationTree.treeData, timeline);
    }

    return {
        populate,
        getAll,
        getHeadings,
        getFootnotes,
        getFigures,
        getTables,
        getLists,
        getType,
    }
}

export type CirrusLandmarksCollection = ReturnType<typeof useCirrusReaderLandmarkCollection>